import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as ArrowRight } from "../../../assets/icons/svg/arrow-right-white.svg";
import loginVideo from "../../../assets/video/login_video.mp4";
import Logo from "../../../assets/icons/png/logo.png";
import ToastContainer, { useToast } from "../../ui/Toast_Message";

const OTP_LENGTH = 6;
const RESEND_TIMEOUT = 90; // in seconds

const OtpPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toasts, showToast } = useToast();

  const email = location?.state?.email || "user****@email.com";
  const [otp, setOtp] = useState<string[]>(new Array(OTP_LENGTH).fill(""));
  const [resendTimer, setResendTimer] = useState(RESEND_TIMEOUT);
  const [isResendDisabled, setIsResendDisabled] = useState(true);

  useEffect(() => {
    if (resendTimer === 0) {
      setIsResendDisabled(false);
      return;
    }

    const timer = setInterval(() => {
      setResendTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [resendTimer]);

  const handleChange = (value: string, index: number) => {
    if (/^\d?$/.test(value)) {
      const updatedOtp = [...otp];
      updatedOtp[index] = value;
      setOtp(updatedOtp);
      if (value && index < OTP_LENGTH - 1) {
        document.getElementById(`otp-${index + 1}`)?.focus();
      }
    }
  };

  const handleSubmit = () => {
    const enteredOtp = otp.join("");

    if (enteredOtp.length !== OTP_LENGTH) return;

    const correctOtp = "123456"; // Replace with real validation or API call

    if (enteredOtp === correctOtp) {
      showToast("success", "OTP Verified", "You have logged in successfully.");
      navigate("/dashboard");
    } else {
      showToast("error", "Invalid OTP", "The OTP you entered is incorrect.");
      setOtp(new Array(OTP_LENGTH).fill("")); // Clear OTP fields
      document.getElementById("otp-0")?.focus(); // Optional: focus first input again
    }
  };

  const handleResend = () => {
    setIsResendDisabled(true);
    setResendTimer(RESEND_TIMEOUT);
    showToast("info", "OTP Resent", `A new OTP has been sent to ${email}`);
  };

  const formattedTime = `${String(Math.floor(resendTimer / 60)).padStart(
    2,
    "0"
  )}:${String(resendTimer % 60).padStart(2, "0")}`;

  return (
    <div className="relative w-full h-screen overflow-hidden">
      {/* Background Video */}
      <video
        className="fixed top-0 left-0 w-full h-full object-cover z-0"
        autoPlay
        loop
        muted
        playsInline
      >
        <source src={loginVideo} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Overlay */}
      <div className="fixed top-0 left-0 w-full h-full bg-[#1F1F1F]/85 z-10" />

      {/* OTP Card */}
      <div className="relative z-20 flex items-center justify-center w-full h-full px-4">
        <div
          className="w-full max-w-xl bg-[#F3F3F3]/20 border border-white/80 rounded-2xl backdrop-blur-md px-6 py-8 sm:py-10 flex flex-col items-center gap-6 overflow-y-auto scrollbar-none"
          style={{ minHeight: "400px", maxHeight: "90vh" }}
        >
          {/* Logo */}
          <img
            src={Logo}
            alt="Acumen Logo"
            className="h-16 sm:h-20 object-contain"
          />

          {/* Header */}
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl font-semibold text-white">
              Login
            </h1>
            <p className="text-white/70 mt-1 text-lg sm:text-lg">
              Enter OTP to authenticate account. OTP sent to email{" "}
              <strong>{email}</strong>
            </p>
          </div>

          {/* OTP Input */}
          <div className="w-full">
            <label className="block text-white mb-2 text-sm sm:text-base">
              OTP
            </label>
            <div className="flex items-center bg-white/20 border border-white/50 rounded-full px-4 py-3 sm:px-6 sm:py-4 backdrop-blur-sm">
              <div className="flex-1 flex justify-between items-center gap-2">
                {otp.map((digit, index) => (
                  <div
                    key={index}
                    className="relative w-full flex justify-center"
                  >
                    <input
                      id={`otp-${index}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleChange(e.target.value, index)}
                      className="w-full bg-transparent border-none text-center text-white text-xl font-semibold outline-none"
                    />
                    {index < OTP_LENGTH - 1 && (
                      <span className="absolute right-0 text-white/40 font-bold select-none pointer-events-none">
                        -
                      </span>
                    )}
                  </div>
                ))}
              </div>

              <button
                onClick={handleSubmit}
                disabled={otp.join("").length !== OTP_LENGTH}
                className={`ml-4 w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300 ${
                  otp.join("").length === OTP_LENGTH
                    ? "bg-[#0091AF] hover:bg-[#007B96]"
                    : "bg-[#333333] cursor-not-allowed"
                }`}
              >
                <ArrowRight className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Resend Option */}
          <div className="text-white/70 text-sm sm:text-base">
            Didn’t receive OTP?{" "}
            <button
              className={`underline ${
                isResendDisabled
                  ? "text-white/40 cursor-not-allowed"
                  : "text-blue-500 hover:text-blue-500/90"
              }`}
              disabled={isResendDisabled}
              onClick={handleResend}
            >
              Re-send
            </button>{" "}
            {isResendDisabled && (
              <span className="ml-1 text-white/40">{formattedTime} s</span>
            )}
          </div>

          {/* Footer */}
          <div className="text-white/50 text-xs sm:text-sm text-center mt-2">
            © Copyright ACUMEN 2025
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} />
    </div>
  );
};

export default OtpPage;
